import { TemplateData, FieldConfig } from '../types/template';

interface GridItem {
  row: number;
  col: number;
  type: 'field' | 'logo' | 'secondary-logo';
  field?: FieldConfig;
  logoUrl?: string;
  logoWidth?: string;
}

/**
 * Generate HTML signature using the working template structure
 */
function generateWorkingTemplateHTML(data: TemplateData, fields: FieldConfig[]): string {
  const { fontFamily, fontSize, primaryColor, logoUrl, logoWidth } = data;
  
  // Use the exact structure from working templates
  let html = `<table style="font-family: ${fontFamily}; font-size: ${fontSize}px; color: ${primaryColor}; border-collapse: collapse;">`;
  html += '<tr><td style="vertical-align: top;">';
  html += '<table cellpadding="0" cellspacing="0" border="0" style="border-collapse: collapse;">';
  
  const enabledFields = fields.filter(f => f.enabled);
  
  enabledFields.forEach((field, index) => {
    const isLastField = index === enabledFields.length - 1;
    const paddingRight = isLastField ? '0' : '15px';
    const fieldWidth = field.gridWidth || 200;
    const fieldHeight = field.gridHeight || 'auto';
    
    html += '<tr style="line-height: 1.2;">';
    html += `<td style="vertical-align: top; width: ${fieldWidth}px; height: ${fieldHeight}; padding-right: ${paddingRight}; padding-bottom: 2px;">`;
    html += generateWorkingFieldHTML(field, data);
    html += '</td>';
    
    // Add logo column with rowspan if this is a logo field or specific position
    if (logoUrl && field.id === 'mobile') {
      const logoRowspan = Math.max(1, enabledFields.length - index);
      html += `<td rowspan="${logoRowspan}" style="vertical-align: top; width: 150px; height: 50px; padding-right: 0; padding-bottom: 2px;">`;
      html += `<img src="${logoUrl}" alt="Company Logo" width="${logoWidth || 164}" style="display: block; margin-bottom: 0px;">`;
      html += '</td>';
    } else if (index < enabledFields.length - 1 && !logoUrl) {
      html += '<td style="width: 150px;"></td>';
    }
    
    html += '</tr>';
  });
  
  html += '</table><br></td></tr></table>';
  return html;
}

function generateWorkingFieldHTML(field: FieldConfig, data: TemplateData): string {
  const styles = [];
  if (field.bold) styles.push('font-weight: bold');
  if (field.italic) styles.push('font-style: italic');
  if (field.color) styles.push(`color: ${field.color}`);
  
  const styleStr = styles.length > 0 ? ` style="${styles.join('; ')}"` : '';
  const prefix = field.prefix || '';
  const prefixHtml = prefix ? `<span style="color: ${field.prefixColor || '#E26A23'}">${prefix}</span>&nbsp;` : '';
  
  if (field.id === 'name' || field.id === 'fullName') {
    return `<span${styleStr}>{{#if givenName}}{{givenName}} {{/if}}{{#if surname}}{{surname}}{{/if}}</span>`;
  } else if (field.id === 'email') {
    return `${prefixHtml}&nbsp;<a href="mailto:{{${field.variable}}}" style="color: #000000 !important; text-decoration: none"><span style="color: #000000 !important">{{${field.variable}}}</span></a>`;
  } else if (field.id === 'website') {
    const websiteValue = field.websiteUrl || data.website || 'www.virtue.dk';
    return `${prefixHtml}&nbsp;<a href="https://${websiteValue}" style="color: #000000 !important; text-decoration: none !important"><span style="color: #000000 !important">${websiteValue}</span></a>`;
  } else if (field.conditional) {
    return `{{#if ${field.variable}}}${prefixHtml}<span${styleStr}>{{${field.variable}}}</span>{{/if}}`;
  } else {
    return `${prefixHtml}<span${styleStr}>{{${field.variable}}}</span>`;
  }
}

/**
 * Generate HTML signature from template configuration
 */
export function generateTemplateHTML(data: TemplateData, defaultFields: FieldConfig[], showGrid: boolean = false): string {
  const fields = data.fields || defaultFields;
  const {
    layout = 'horizontal',
    layoutMode = 'list',
    logoUrl = '',
    logoWidth = '100',
    logoPosition = 'left',
    logoAlignToField = 'name',
    secondaryLogoUrl = '',
    secondaryLogoWidth = '80',
    showSecondaryLogo = false,
    fontFamily = 'Arial, sans-serif',
    fontSize = '14',
    primaryColor = '#000000'
  } = data;

  if (layoutMode === 'list') {
    // List mode actually generates proper grid layouts (working templates use this)
    return generateGridBasedLayout(fields, data, fontSize, primaryColor, showGrid);
  } else {
    // Grid mode generates list layouts (this seems counterintuitive but matches existing behavior)
    return generateListBasedLayout(fields, data, fontSize, primaryColor, logoUrl, logoWidth, secondaryLogoUrl, secondaryLogoWidth, showSecondaryLogo, logoPosition, logoAlignToField);
  }
}

function buildAlignedLogoLayout(
  fields: FieldConfig[],
  data: TemplateData,
  fontSize: string,
  primaryColor: string,
  logoUrl: string,
  logoWidth: string,
  secondaryLogoUrl: string,
  secondaryLogoWidth: string,
  hasSecondaryLogo: boolean,
  alignToFieldId: string,
  logoSide: 'left' | 'right'
): string {
  const enabledFields = fields.filter(f => f.enabled);
  const alignFieldIndex = enabledFields.findIndex(f => f.id === alignToFieldId);
  
  // If align field not found, fall back to regular column layout
  if (alignFieldIndex < 0) {
    return buildLogoColumn(logoSide === 'left', hasSecondaryLogo, logoUrl, logoWidth, secondaryLogoUrl, secondaryLogoWidth) +
           '<td style="vertical-align: top;">' +
           generateListLayout(fields, data, fontSize, primaryColor) +
           '</td>';
  }
  
  const maxLogoWidth = Math.max(
    parseInt(logoWidth),
    hasSecondaryLogo ? parseInt(secondaryLogoWidth) : 0
  );
  
  // Build logo HTML
  let logoHtml = '';
  if (logoUrl) {
    logoHtml += `<img src="${logoUrl}" alt="Logo" width="${logoWidth}" style="display: block; margin-bottom: ${hasSecondaryLogo ? '10' : '0'}px;">`;
  }
  if (hasSecondaryLogo && secondaryLogoUrl) {
    logoHtml += `<img src="${secondaryLogoUrl}" alt="Secondary Logo" width="${secondaryLogoWidth}" style="display: block;">`;
  }
  
  // Use nested table with rowspan for precise alignment
  let html = '<td style="vertical-align: top; padding: 0;"><table style="border-collapse: collapse;">';
  
  let skipNext = false;
  let adjustedAlignIndex = alignFieldIndex;
  
  enabledFields.forEach((field, index) => {
    if (skipNext) {
      skipNext = false;
      return;
    }
    
    // Check if this is firstName and next is lastName - combine them
    const nextField = enabledFields[index + 1];
    const combineWithNext = field.id === 'firstName' && nextField?.id === 'lastName';
    
    // Adjust align index if we're combining fields before the align point
    if (combineWithNext && index < alignFieldIndex) {
      adjustedAlignIndex--;
    }
    
    html += '<tr>';
    
    // Add logo cell on the align field row
    if (index === adjustedAlignIndex || (combineWithNext && index + 1 === adjustedAlignIndex)) {
      const remainingRows = enabledFields.length - index - (combineWithNext ? 1 : 0);
      if (logoSide === 'left') {
        html += `<td rowspan="${remainingRows}" style="padding-right: 10px; vertical-align: top; width: ${maxLogoWidth}px;"><div style="height: 0; overflow: visible;">${logoHtml}</div></td>`;
      }
      if (combineWithNext) {
        html += '<td style="vertical-align: top;">' + generateFieldHTML(field, data, fontSize, primaryColor) + generateFieldHTML(nextField, data, fontSize, primaryColor) + '</td>';
        skipNext = true;
      } else {
        html += '<td style="vertical-align: top;">' + generateFieldHTML(field, data, fontSize, primaryColor) + '</td>';
      }
      if (logoSide === 'right') {
        html += `<td rowspan="${remainingRows}" style="padding-left: 15px; vertical-align: top; width: ${maxLogoWidth}px;"><div style="height: 0; overflow: visible;">${logoHtml}</div></td>`;
      }
    } else if (index < adjustedAlignIndex) {
      // Fields before the align field - need empty cell for logo column
      if (logoSide === 'left') {
        html += '<td style="width: ' + maxLogoWidth + 'px;"></td>';
      }
      if (combineWithNext) {
        html += '<td style="vertical-align: top;">' + generateFieldHTML(field, data, fontSize, primaryColor) + generateFieldHTML(nextField, data, fontSize, primaryColor) + '</td>';
        skipNext = true;
      } else {
        html += '<td style="vertical-align: top;">' + generateFieldHTML(field, data, fontSize, primaryColor) + '</td>';
      }
      if (logoSide === 'right') {
        html += '<td style="width: ' + maxLogoWidth + 'px;"></td>';
      }
    } else {
      // Fields after align field - logo already spans these rows
      if (combineWithNext) {
        html += '<td style="vertical-align: top;">' + generateFieldHTML(field, data, fontSize, primaryColor) + generateFieldHTML(nextField, data, fontSize, primaryColor) + '</td>';
        skipNext = true;
      } else {
        html += '<td style="vertical-align: top;">' + generateFieldHTML(field, data, fontSize, primaryColor) + '</td>';
      }
    }
    
    html += '</tr>';
  });
  
  html += '</table></td>';
  
  return html;
}

function buildLogoColumn(
  hasLeftLogo: boolean, 
  hasLeftSecondary: boolean, 
  logoUrl: string, 
  logoWidth: string, 
  secondaryLogoUrl: string, 
  secondaryLogoWidth: string
): string {
  if (!hasLeftLogo && !hasLeftSecondary) return '';
  
  const maxWidth = Math.max(
    hasLeftLogo ? parseInt(logoWidth) : 0,
    hasLeftSecondary ? parseInt(secondaryLogoWidth) : 0
  );
  
  let logoHtml = `<td style="padding-right: 10px; vertical-align: top; width: ${maxWidth}px;">`;
  if (hasLeftLogo) {
    logoHtml += `<img src="${logoUrl}" alt="Logo" width="${logoWidth}" style="display: block; margin-bottom: ${hasLeftSecondary ? '10' : '0'}px;">`;
  }
  if (hasLeftSecondary) {
    logoHtml += `<img src="${secondaryLogoUrl}" alt="Secondary Logo" width="${secondaryLogoWidth}" style="display: block;">`;
  }
  logoHtml += '</td>';
  return logoHtml;
}

function buildRightLogoCell(
  hasRightLogo: boolean,
  hasRightSecondary: boolean,
  logoUrl: string,
  logoWidth: string,
  secondaryLogoUrl: string,
  secondaryLogoWidth: string
): string {
  if (!hasRightLogo && !hasRightSecondary) return '';
  
  const maxWidth = Math.max(
    hasRightLogo ? parseInt(logoWidth) : 0,
    hasRightSecondary ? parseInt(secondaryLogoWidth) : 0
  );
  
  let logoHtml = `<td style="padding-left: 15px; vertical-align: top; width: ${maxWidth}px;">`;
  if (hasRightLogo) {
    logoHtml += `<img src="${logoUrl}" alt="Logo" width="${logoWidth}" style="display: block; margin-bottom: ${hasRightSecondary ? '10' : '0'}px;">`;
  }
  if (hasRightSecondary) {
    logoHtml += `<img src="${secondaryLogoUrl}" alt="Secondary Logo" width="${secondaryLogoWidth}" style="display: block;">`;
  }
  logoHtml += '</td>';
  return logoHtml;
}

function generateGridBasedLayout(fields: FieldConfig[], data: TemplateData, fontSize: string, primaryColor: string, showGrid: boolean = false): string {
  // Build a single table grid where all fields use row/column positioning with rowspan support
  // This is fully email-compatible (works in Outlook) using standard HTML tables
  
  const COLUMN_WIDTH = 200; // Standard width per column to match old templates
  
  // Filter to only enabled fields first
  const enabledFields = fields.filter(f => f.enabled);
  
  // Process enabled fields with their grid positions
  const fieldsWithPosition = enabledFields.map((field, index) => {
    // Use gridCol if set, otherwise default to column 1
    const col = field.gridCol || 1;
    
    // Use gridRow if set, otherwise auto-assign based on order
    const row = field.gridRow || (index + 1);
    
    // Default rowspan is 1
    const rowspan = field.gridRowspan || 1;
    
    return { ...field, gridRow: row, gridCol: col, gridRowspan: rowspan };
  });
  
  // Find grid dimensions - we'll use double the rows to add spacers
  const contentMaxRow = Math.max(...fieldsWithPosition.map(f => (f.gridRow || 1)));
  const maxCol = Math.max(...fieldsWithPosition.map(f => f.gridCol || 1));
  
  // Calculate actual max row accounting for rowspan
  const actualMaxRow = Math.max(...fieldsWithPosition.map(f => (f.gridRow || 1) + (f.gridRowspan || 1) - 1));
  
  // Create a map to track field placement and which cells are occupied
  interface CellInfo {
    field: FieldConfig;
    rowspan: number;
  }
  const gridMap = new Map<string, CellInfo>();
  const occupiedCells = new Set<string>();
  
  // Place all fields in the grid and mark occupied cells
  // Direct row mapping - Row 1 = table row 1, Row 2 = table row 2, etc.
  fieldsWithPosition.forEach(field => {
    const row = field.gridRow || 1;
    const col = field.gridCol || 1;
    const rowspan = field.gridRowspan || 1;
    
    const key = `${row}-${col}`;
    gridMap.set(key, { field, rowspan });
    
    // Mark all cells this field occupies (for rowspan tracking)
    for (let r = row; r < row + rowspan; r++) {
      occupiedCells.add(`${r}-${col}`);
    }
  });
  
  // Build the HTML table - direct row mapping without spacers
  const tableBorder = showGrid ? '1' : '0';
  const tableStyle = 'border-collapse: collapse;';
  let html = `<table cellpadding="0" cellspacing="0" border="${tableBorder}" style="${tableStyle}">`;
  
  // Only render if there are any grid items
  if (actualMaxRow === 0) {
    html += '</table>';
    return html;
  }
  
  for (let row = 1; row <= actualMaxRow; row++) {
    // Check if this row has any content
    let rowHasContent = false;
    for (let col = 1; col <= maxCol; col++) {
      const key = `${row}-${col}`;
      if (gridMap.has(key) || occupiedCells.has(key)) {
        rowHasContent = true;
        break;
      }
    }
    
    // Always skip empty rows (showGrid only affects visual borders, not structure)
    if (!rowHasContent) {
      continue;
    }
    
    html += '<tr style="line-height: 1.2;">';
    
    for (let col = 1; col <= maxCol; col++) {
      const key = `${row}-${col}`;
      
      // Skip if this cell is occupied by a rowspan from a previous row
      if (occupiedCells.has(key) && !gridMap.has(key)) {
        continue;
      }
      
      const cellInfo = gridMap.get(key);
      
      if (cellInfo) {
        // Cell has a field - render it
        const { field, rowspan } = cellInfo;
        const fieldWidth = field.gridWidth || COLUMN_WIDTH;
        const fieldHeight = field.gridHeight ? `${field.gridHeight}px` : field.gridRowspan && field.gridRowspan > 1 ? '50px' : 'auto';
        const paddingRight = col < maxCol ? '15px' : '0';
        const paddingBottom = '2px'; // Minimal spacing between rows
        const cellBorder = '';
        const cellBg = '';
        
        const rowspanAttr = rowspan > 1 ? ` rowspan="${rowspan}"` : '';
        html += `<td${rowspanAttr} style="vertical-align: top; width: ${fieldWidth}px; height: ${fieldHeight}; padding-right: ${paddingRight}; padding-bottom: ${paddingBottom}; ${cellBorder} ${cellBg}">`;
        html += generateFieldHTML(field, data, fontSize, primaryColor, undefined, true); // Pass true for isGridMode
        html += '</td>';
      } else {
        // Empty cell - always render to maintain grid structure, but make invisible when grid is off
        const cellBorder = showGrid ? 'border: 1px solid #ddd;' : '';
        html += `<td style="width: ${COLUMN_WIDTH}px; ${cellBorder}"></td>`;
      }
    }
    
    html += '</tr>';
  }
  
  html += '</table>';
  return html;
}

function generateListBasedLayout(
  fields: FieldConfig[],
  data: TemplateData,
  fontSize: string,
  primaryColor: string,
  logoUrl: string,
  logoWidth: string,
  secondaryLogoUrl: string,
  secondaryLogoWidth: string,
  showSecondaryLogo: boolean,
  logoPosition: string,
  logoAlignToField: string
): string {
  const {
    fontFamily = 'Arial, sans-serif'
  } = data;

  // Use the outer table structure that matches working templates
  let html = `<table style="font-family: ${fontFamily}; font-size: ${fontSize}px; color: ${primaryColor}; border-collapse: collapse;">`;
  html += '<tr><td style="vertical-align: top;">';

  // Check if we need logo alignment logic
  if (logoUrl && logoPosition && logoAlignToField) {
    html += buildAlignedLogoLayout(fields, data, fontSize, primaryColor, logoUrl, logoWidth, secondaryLogoUrl, secondaryLogoWidth, showSecondaryLogo, logoAlignToField, logoPosition as 'left' | 'right');
  } else if (logoUrl && logoPosition === 'left') {
    // Simple left logo layout
    html += buildLogoColumn(true, showSecondaryLogo, logoUrl, logoWidth, secondaryLogoUrl, secondaryLogoWidth);
    html += '<td style="vertical-align: top;">';
    html += generateListLayout(fields, data, fontSize, primaryColor);
    html += '</td>';
  } else {
    // No logo or right logo
    html += '<td style="vertical-align: top;">';
    html += generateListLayout(fields, data, fontSize, primaryColor);
    html += '</td>';
    if (logoUrl && logoPosition === 'right') {
      html += buildRightLogoCell(true, showSecondaryLogo, logoUrl, logoWidth, secondaryLogoUrl, secondaryLogoWidth);
    }
  }
  
  html += '</td></tr></table>';
  return html;
}

function generateListLayout(fields: FieldConfig[], data: TemplateData, fontSize: string, primaryColor: string): string {
  const enabledFields = fields.filter(f => f.enabled);
  
  // In list mode, always render fields as a simple vertical list (like the old templates)
  // This ignores grid positioning and column breaks for simplicity
  let html = '';
  enabledFields.forEach((field) => {
    html += generateFieldHTML(field, data, fontSize, primaryColor);
  });
  
  return html;
}

function generateGridLayout(
  data: TemplateData,
  fields: FieldConfig[],
  fontSize: string,
  primaryColor: string,
  logoUrl: string,
  logoWidth: string,
  secondaryLogoUrl: string,
  secondaryLogoWidth: string,
  showSecondaryLogo: boolean,
  showGrid: boolean = false
): string {
  // Get all enabled fields and auto-assign row/col if not set
  const enabledFieldsRaw = fields.filter(f => f.enabled);
  
  // Find the max row used by fields with explicit positions
  const maxExplicitRow = Math.max(0, ...enabledFieldsRaw.filter(f => f.row !== undefined).map(f => f.row!));
  
  // Auto-assign row/col for fields that don't have them
  let nextAutoRow = maxExplicitRow + 1;
  const enabledFields = enabledFieldsRaw.map(f => {
    if (f.row !== undefined && f.col !== undefined) {
      return f;
    }
    // Auto-assign to column 0, next available row
    const assigned = { ...f, row: nextAutoRow, col: 0 };
    nextAutoRow++;
    return assigned;
  }).filter(f => f.row !== undefined && f.col !== undefined);
  const gridItems: GridItem[] = [];
  
  // Add fields to grid
  enabledFields.forEach(field => {
    gridItems.push({
      row: field.row!,
      col: field.col!,
      type: 'field',
      field,
    });
  });
  
  // Add primary logo to grid if positioned
  if (logoUrl && data.logoGridRow !== undefined && data.logoGridCol !== undefined) {
    gridItems.push({
      row: data.logoGridRow,
      col: data.logoGridCol,
      type: 'logo',
      logoUrl,
      logoWidth,
    });
  }
  
  // Add secondary logo to grid if positioned
  if (showSecondaryLogo && secondaryLogoUrl && data.secondaryLogoGridRow !== undefined && data.secondaryLogoGridCol !== undefined) {
    gridItems.push({
      row: data.secondaryLogoGridRow,
      col: data.secondaryLogoGridCol,
      type: 'secondary-logo',
      logoUrl: secondaryLogoUrl,
      logoWidth: secondaryLogoWidth,
    });
  }
  
  // Determine grid dimensions
  const rows = data.gridRows || 3;
  const cols = data.gridCols || 2;
  
  // Create a 2D map of grid cells
  const grid: Map<string, GridItem> = new Map();
  gridItems.forEach(item => {
    const key = `${item.row}-${item.col}`;
    grid.set(key, item);
  });
  
  // Check which columns have any content
  const colsWithContent = new Set<number>();
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      if (grid.has(`${r}-${c}`)) {
        colsWithContent.add(c);
      }
    }
  }
  
  // Only render rows that have content
  const rowsWithContent = new Set<number>();
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      if (grid.has(`${r}-${c}`)) {
        rowsWithContent.add(r);
      }
    }
  }
  
  // Debug: If no content found, fall back to simple grid
  if (rowsWithContent.size === 0 && enabledFields.length > 0) {
    console.log('Grid layout: No content found, using fallback simple grid');
    let fallbackHtml = '<table style="border-collapse: collapse;">';
    enabledFields.forEach((field, index) => {
      fallbackHtml += '<tr>';
      fallbackHtml += '<td style="vertical-align: top; padding-right: 25px;">';
      fallbackHtml += generateFieldHTML(field, data, fontSize, primaryColor, undefined, true);
      fallbackHtml += '</td>';
      fallbackHtml += '</tr>';
    });
    fallbackHtml += '</table>';
    return fallbackHtml;
  }
  
  // Generate table matching the working template structure
  const tableStyle = showGrid ? 'border-collapse: collapse; border: 2px solid #0066cc;' : 'border-collapse: collapse;';
  const tableBorder = showGrid ? '1' : '0';
  let html = `<table cellpadding="0" cellspacing="0" border="${tableBorder}" style="${tableStyle}">`;
  
  // Render grid rows with proper styling like working template
  Array.from(rowsWithContent).sort((a, b) => a - b).forEach(r => {
    html += '<tr style="line-height: 1.2;">';
    
    Array.from(colsWithContent).sort((a, b) => a - b).forEach((c, colIndex) => {
      const key = `${r}-${c}`;
      const item = grid.get(key);
      const isLastCol = colIndex === colsWithContent.size - 1;
      
      if (item) {
        // Cell with content - match working template style
        const fieldWidth = item.field?.gridWidth || 200;
        const fieldHeight = item.field?.gridHeight || 'auto';
        const paddingRight = !isLastCol ? '15px' : '0';
        const paddingBottom = '2px';
        
        const cellStyle = `vertical-align: top; width: ${fieldWidth}px; height: ${fieldHeight}; padding-right: ${paddingRight}; padding-bottom: ${paddingBottom};`;
        
        html += `<td style="${cellStyle}">`;
        if (item.type === 'field' && item.field) {
          html += generateFieldHTML(item.field, data, fontSize, primaryColor, undefined, true);
        } else if ((item.type === 'logo' || item.type === 'secondary-logo') && item.logoUrl) {
          html += `<img src="${item.logoUrl}" alt="${item.type === 'logo' ? 'Logo' : 'Secondary Logo'}" width="${item.logoWidth}" style="display: block; margin-bottom: 0px;">`;
        }
        html += '</td>';
      } else if (showGrid) {
        // Empty cell when showing grid
        html += `<td style="width: 150px; border: 1px dashed #ccc;"></td>`;
      }
    });
    
    html += '</tr>';
  });
  
  html += '</table>';
  return html;
}

function generateFieldHTML(field: FieldConfig, data: TemplateData, fontSize: string, primaryColor: string, nextField?: FieldConfig, isGridMode: boolean = false): string {
  let fieldHtml = '';
  const styles: string[] = [];
  
  // Apply left margin if set
  const leftMargin = field.leftMargin || 0;
  const marginStyle = leftMargin > 0 ? `margin-left: ${leftMargin}px;` : '';
  
  // Apply custom or default styling
  if (field.id === 'name') {
    styles.push(`font-size: ${parseInt(fontSize) + 2}px`);
    styles.push('font-weight: bold');
  }
  
  if (field.bold) styles.push('font-weight: bold');
  if (field.italic) styles.push('font-style: italic');
  if (field.underline) styles.push('text-decoration: underline');
  if (field.color) styles.push(`color: ${field.color}`);
  if (field.fontFamily) styles.push(`font-family: ${field.fontFamily}`);
  if (field.fontSize) styles.push(`font-size: ${field.fontSize}px`);
  
  // Default colors for certain fields if no custom color
  if (!field.color && (field.id === 'jobTitle' || field.id === 'department')) {
    styles.push('color: #666');
  }
  
  const styleStr = styles.length > 0 ? styles.join('; ') : '';
  const prefix = field.prefix || '';
  // Convert spaces to &nbsp; to preserve them in HTML
  const prefixEscaped = prefix.replace(/ /g, '&nbsp;');
  const prefixWithColor = prefixEscaped && field.prefixColor 
    ? `<span style="color: ${field.prefixColor}">${prefixEscaped}</span>&nbsp;`
    : prefixEscaped ? `${prefixEscaped}&nbsp;` : '';
  
  const openTag = styleStr ? `<span style="${styleStr}">` : '<span>';
  const closeTag = '</span>';
  
  // Handle different field types
  if (field.variable === 'logo' || field.id === 'logo') {
    // Use logoUrl and logoWidth from field config, or fall back to template data
    const logoUrl = field.logoUrl || data.logoUrl || '';
    const logoWidth = field.logoWidth || data.logoWidth || 100;
    if (logoUrl) {
      // No margin-bottom when logo uses rowspan (it's in grid mode)
      const marginBottom = field.gridRowspan && field.gridRowspan > 1 ? '0' : '10';
      fieldHtml = `<img src="${logoUrl}" alt="Company Logo" width="${logoWidth}" style="display: block; margin-bottom: ${marginBottom}px;">`;
    }
  } else if (field.variable === 'secondaryLogo' || field.id === 'secondaryLogo') {
    // Use logoUrl and logoWidth from field config, or fall back to template data
    const logoUrl = field.logoUrl || data.secondaryLogoUrl || '';
    const logoWidth = field.logoWidth || data.secondaryLogoWidth || 80;
    if (logoUrl) {
      // No margin-bottom when logo uses rowspan (it's in grid mode)
      const marginBottom = field.gridRowspan && field.gridRowspan > 1 ? '0' : '10';
      fieldHtml = `<img src="${logoUrl}" alt="Secondary Logo" width="${logoWidth}" style="display: block; margin-bottom: ${marginBottom}px;">`;
    }
  } else if (field.variable === 'profilePicture' || field.id === 'profilePicture') {
    fieldHtml = `{{#if profilePicture}}<img src="{{profilePicture}}" alt="Profile" width="80" height="80" style="border-radius: 50%; display: block; margin-bottom: 10px;">{{/if}}`;
  } else if (field.variable === 'spacer' || field.id.startsWith('spacer')) {
    fieldHtml = '<br>';
  } else if (field.variable === 'customText' || field.id.startsWith('text_')) {
    const textValue = field.customText || data.customTexts?.[field.id] || '';
    if (textValue) {
      const lineBreak = isGridMode ? '' : '<br>';
      // Convert line breaks to <br> tags for HTML display
      const formattedText = textValue.replace(/\n/g, '<br>');
      fieldHtml = `${prefixWithColor}${openTag}${formattedText}${closeTag}${lineBreak}`;
    }
  } else if (field.id === 'email' || field.id === 'sharedEmail') {
    const emailPrefixText = prefixEscaped || '';
    const emailPrefixColored = emailPrefixText
      ? (field.prefixColor 
          ? `<span style="color: ${field.prefixColor}">${emailPrefixText}</span>&nbsp;`
          : `${emailPrefixText}&nbsp;`)
      : '';
    const lineBreak = isGridMode ? '' : '<br>';
    fieldHtml = `${emailPrefixColored}<a href="mailto:{{${field.variable}}}" style="${styleStr || `color: ${primaryColor} !important; text-decoration: none`}"><span style="color: ${field.color || primaryColor} !important">{{${field.variable}}}</span></a>${lineBreak}`;
  } else if (field.id === 'company') {
    const lineBreak = isGridMode ? '' : '<br>';
    fieldHtml = `${prefixWithColor}${openTag}${data.companyName}${closeTag}${lineBreak}`;
  } else if (field.id === 'vat') {
    if (data.vatNumber) {
      const lineBreak = isGridMode ? '' : '<br>';
      fieldHtml = `${prefixWithColor}${openTag}${data.vatNumber}${closeTag}${lineBreak}`;
    }
  } else if (field.id === 'website') {
    // Use field's websiteUrl if set, otherwise fall back to template data
    const websiteValue = field.websiteUrl || data.website || '';
    if (websiteValue) {
      const websiteUrl = websiteValue.startsWith('http://') || websiteValue.startsWith('https://') 
        ? websiteValue 
        : `https://${websiteValue}`;
      // Display just the domain without protocol for cleaner look
      const displayValue = websiteValue.replace(/^https?:\/\//, '');
      const lineBreak = isGridMode ? '' : '<br>';
      fieldHtml = `${prefixWithColor}<a href="${websiteUrl}" style="${styleStr || `color: ${primaryColor} !important; text-decoration: none !important`}"><span style="color: ${field.color || primaryColor} !important">${displayValue}</span></a>${lineBreak}`;
    }
  } else if (field.id === 'fullName') {
    // Combine givenName and surname
    const lineBreak = isGridMode ? '' : '<br>';
    fieldHtml = `${prefixWithColor}${openTag}{{#if givenName}}{{givenName}} {{/if}}{{#if surname}}{{surname}}{{/if}}${closeTag}${lineBreak}`;
  } else if (field.id === 'address' && (data.addressLine1 || data.addressLine2)) {
    fieldHtml = '';
    if (data.addressLine1) fieldHtml += data.addressLine1 + (isGridMode ? '' : '<br>');
    if (data.addressLine2) fieldHtml += data.addressLine2 + (isGridMode ? '' : '<br>');
  } else if (field.conditional) {
    // Grid mode: no line breaks, just the content
    fieldHtml = `{{#if ${field.variable}}}${prefixWithColor}${openTag}{{${field.variable}}}${closeTag}{{/if}}`;
  } else {
    // Grid mode: no line breaks, just the content
    fieldHtml = `${prefixWithColor}${openTag}{{${field.variable}}}${closeTag}`;
  }
  
  // Check if next field wants to be on same line - if so, remove trailing <br>
  if (nextField?.sameLine && fieldHtml.endsWith('<br>')) {
    fieldHtml = fieldHtml.slice(0, -4); // Remove <br>
  }
  
  // Wrap with margin and inline-block if leftMargin is set
  // This allows fields to sit side-by-side on the same line
  if (marginStyle) {
    fieldHtml = `<div style="${marginStyle} display: inline-block; vertical-align: top;">${fieldHtml}</div>`;
  }
  
  return fieldHtml;
}

function generateSocialIcons(data: TemplateData): string {
  if (!data.showSocialIcons) return '';
  
  const socials = [];
  if (data.socialLinks.linkedin) {
    socials.push(`<a href="${data.socialLinks.linkedin}"><img src="https://cdn-icons-png.flaticon.com/32/174/174857.png" alt="LinkedIn" width="20" height="20" style="margin-right: 5px;"></a>`);
  }
  if (data.socialLinks.twitter) {
    socials.push(`<a href="${data.socialLinks.twitter}"><img src="https://cdn-icons-png.flaticon.com/32/733/733579.png" alt="Twitter" width="20" height="20" style="margin-right: 5px;"></a>`);
  }
  if (data.socialLinks.facebook) {
    socials.push(`<a href="${data.socialLinks.facebook}"><img src="https://cdn-icons-png.flaticon.com/32/733/733547.png" alt="Facebook" width="20" height="20" style="margin-right: 5px;"></a>`);
  }
  
  if (socials.length > 0) {
    return '<br>' + socials.join('');
  }
  
  return '';
}
